import type { TimesheetEntryWithTaskCode } from '@renewal/types';
import { badgePresetFor } from './badgeStyles.js';
import { getContrastColor, hslToRgb, stringToColor, stringToHsl } from './blockColors.js';
import './TaskColorLegend.css';

interface TaskColorLegendProps {
  entries: TimesheetEntryWithTaskCode[];
}

/**
 * TaskColorLegend shows a legend of task codes and their colors
 * Only shows task codes that are used in the current timesheet
 */
export function TaskColorLegend({ entries }: TaskColorLegendProps) {
  // Get unique task codes from entries. A code with a `badgeStyle` preset renders
  // the fixed preset color + glyph; otherwise it falls back to the hash→HSL color.
  const taskCodeMap = new Map<
    string,
    { code: string; name: string; color: string; glyph: string | null; textColor: string }
  >();

  for (const entry of entries) {
    if (!taskCodeMap.has(entry.taskCodeId)) {
      const preset = badgePresetFor(entry.taskCode.badgeStyle);
      const { hue, saturation, lightness } = stringToHsl(entry.taskCodeId);
      taskCodeMap.set(entry.taskCodeId, {
        code: entry.taskCode.code,
        name: entry.taskCode.name,
        color: preset ? preset.color : stringToColor(entry.taskCodeId),
        glyph: preset ? preset.glyph : null,
        // Preset colors are fixed/dark, so contrast against white text; a hash
        // colour gets whichever of dark and white measures higher against it.
        textColor: preset ? '#ffffff' : getContrastColor(hslToRgb(hue, saturation, lightness)),
      });
    }
  }

  const taskCodes = Array.from(taskCodeMap.values()).sort((a, b) => a.code.localeCompare(b.code));

  if (taskCodes.length === 0) {
    return null;
  }

  return (
    <div className="task-color-legend" data-testid="task-color-legend">
      <h4 className="legend-title">Task Colors</h4>
      <div className="legend-items">
        {taskCodes.map((task) => (
          <div key={task.code} className="legend-item" data-testid={`legend-item-${task.code}`}>
            <span
              className="legend-color"
              style={{
                backgroundColor: task.color,
                color: task.textColor,
              }}
            >
              {task.glyph ? `${task.glyph} ${task.code}` : task.code}
            </span>
            <span className="legend-name">{task.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
